import React, { useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Switch,
  Cascader,
  Row,
  Col,
  Spin,
} from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryApi } from "../store/actions/common";
import { queryClient } from "../store/queryClient";
import { triggerNotification } from "../store/directActions";
import SingleUpload from "../components/SingleUpload";
import { connect, ConnectedProps } from "react-redux";

type UserRole = "admin" | "store";

interface ProfilePageProps {
  userRole?: UserRole;
}

const ProfilePage: React.FC<ProfilePageProps> = ({ countryStateData }) => {
  const [profileForm] = Form.useForm();
  const [storeForm] = Form.useForm();

  // Fetch user profile data
  const { data: profileData, isPending: isLoadingProfile } = useQuery({
    queryKey: ["auth", "profile", "user"],
    queryFn: () => queryApi(`auth/profile/user`, "GET"),
    staleTime: Infinity,
    gcTime: 0,
  });

  // Fetch store data
  const { data: storeData, isPending: isLoadingStore } = useQuery({
    queryKey: ["store", "user"],
    queryFn: () => queryApi(`store/user`, "GET"),
    staleTime: Infinity,
    gcTime: 0,
  });

  // Update profile form when data is loaded
  useEffect(() => {
    if (profileData) {
      // @ts-ignore - queryApi returns data directly despite ApiResponse type signature
      const userData: any = profileData;
      profileForm.setFieldsValue({
        name: userData?.name || "",
        email: userData?.email || "",
        phone: userData?.phone || userData?.mobile || "",
        password: "", // Don't populate password field
      });
    }
  }, [profileData, profileForm]);

  // Update store form when data is loaded
  useEffect(() => {
    if (storeData && Array.isArray(storeData) && storeData.length > 0) {
      // @ts-ignore - queryApi returns data directly despite ApiResponse type signature
      const store: any = storeData[0];
      storeForm.setFieldsValue({
        name: store?.name || "",
        store_phone: store?.store_phone || "",
        country_state:
          store?.country_id && store?.state_id
            ? [store.country_id, store.state_id]
            : [],
        city: store?.city || "",
        street: store?.street || "",
        zip: store?.zip || "",
        lottery_retailer_no: store?.lottery_retailer_no || "",
        store_meta_1: store?.store_meta_1 || "",
        store_meta_2: store?.store_meta_2 || "",
        store_logo: store?.store_logo || "",
        show_header: store?.show_header === 1 || store?.show_header === true,
      });
    }
  }, [storeData, storeForm]);

  // Mutation for updating profile
  const { mutate: updateProfile, isPending: isUpdatingProfile } = useMutation({
    mutationFn: (data: { name: string; password?: string }) =>
      queryApi(`auth/profile/user/name`, "PUT", data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["auth", "profile", "user"],
      });
      profileForm.setFieldsValue({ password: "" }); // Clear password field after save
      triggerNotification({
        title: "Success",
        description: "Profile updated successfully",
        type: "success",
      });
    },
    onError: (error: any) => {
      triggerNotification({
        title: "Error",
        description: error.message || "Failed to update profile",
        type: "error",
      });
    },
  });

  // Mutation for updating store
  const { mutate: updateStore, isPending: isUpdatingStore } = useMutation({
    mutationFn: (data: any) => queryApi(`store/`, "PUT", data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["store", "user"],
      });
      triggerNotification({
        title: "Success",
        description: "Store settings updated successfully",
        type: "success",
      });
    },
    onError: (error: any) => {
      triggerNotification({
        title: "Error",
        description: error.message || "Failed to update store settings",
        type: "error",
      });
    },
  });

  const handleSaveProfile = (values: any) => {
    const updateData: { name: string; password?: string } = {
      name: values.name,
    };

    // Only include password if it's provided and not empty
    if (values.password && values.password.trim() !== "") {
      updateData.password = values.password;
    }

    updateProfile(updateData);
  };

  const handleSaveStore = (values: any) => {
    const updateData: any = {
      ...values,
      show_header: values.show_header ? 1 : 0,
      country_id:
        values.country_state && values.country_state.length > 0
          ? values.country_state[0]
          : null,
      state_id:
        values.country_state && values.country_state.length > 1
          ? values.country_state[1]
          : null,
      store_logo: values?.store_logo?.url
        ? values.store_logo.url
        : values.store_logo,
    };

    // Remove country_state from the data as we've extracted it
    delete updateData.country_state;

    updateStore(updateData);
  };

  const filterCountryState = (inputValue: string, path: any[]) => {
    return path.some(
      (option) =>
        option.label.toLowerCase().indexOf(inputValue.toLowerCase()) > -1,
    );
  };

  if (isLoadingProfile || isLoadingStore) {
    return (
      <div className="page-section h-page p-6 flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="page-section h-page p-6 space-y-6 flex flex-col">
      {/* Header */}

      <Row gutter={[24, 24]}>
        {/* Profile Information */}
        <Col xs={24}>
          <Card
            title="Profile Information"
            className="app-surface backdrop-blur-xl shadow-xl app-border"
          >
            <Form
              form={profileForm}
              layout="vertical"
              onFinish={handleSaveProfile}
            >
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Full Name"
                    name="name"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your full name",
                      },
                    ]}
                  >
                    <Input size="large" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Email Address"
                    name="email"
                    rules={[
                      { required: true, message: "Please enter your email" },
                      { type: "email", message: "Please enter a valid email" },
                    ]}
                  >
                    <Input size="large" disabled />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Phone Number"
                    name="phone"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your phone number",
                      },
                    ]}
                  >
                    <Input size="large" disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Password"
                    name="password"
                    help="Leave empty to keep current password"
                  >
                    <Input.Password
                      size="large"
                      placeholder="Enter new password"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item style={{ margin: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isUpdatingProfile}
                  icon={<SaveOutlined />}
                  size="large"
                >
                  Save Changes
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>

      {/* Store Settings */}
      <Card
        title="Store Settings"
        className="app-surface backdrop-blur-xl shadow-xl app-border"
      >
        <Form
          form={storeForm}
          layout="vertical"
          onFinish={handleSaveStore}
          hideRequiredMark
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: "Please enter store name" }]}
              >
                <Input placeholder="Please enter store name" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="store_phone"
                label="Mobile"
                rules={[
                  { required: true, message: "Please enter store mobile" },
                ]}
              >
                <Input placeholder="Please enter store phone" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="country_state" label="Country/State">
                <Cascader
                  // @ts-ignore
                  options={countryStateData || []}
                  placeholder="Please select country and state"
                  showSearch={{ filter: filterCountryState }}
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="city" label="City">
                <Input placeholder="Please enter city name" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="street" label="Street">
                <Input placeholder="Please enter street name" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="zip" label="Zip Code">
                <Input placeholder="Please enter zip code" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="lottery_retailer_no" label="Retailer No">
                <Input
                  placeholder="Please enter retailer number"
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="store_meta_1" label="Store Banner 1">
                <Input placeholder="Please enter anything" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="store_meta_2" label="Store Banner 2">
                <Input placeholder="Please enter anything" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="store_logo"
                label="Store Logo"
                getValueFromEvent={(e) => {
                  if (typeof e === "string") return e;
                  return e?.url || e;
                }}
              >
                {/* @ts-ignore - Form.Item provides value and onChange automatically */}
                <SingleUpload hideText={false} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="show_header"
                valuePropName="checked"
                label="Show header"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={isUpdatingStore}
              icon={<SaveOutlined />}
              size="large"
            >
              Save Store Settings
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  // Map state properties to props here if needed

  countryStateData:
    state.cms?.lookup?.country?.map((country) => {
      return {
        ...country,
        value: country.id,
        label: country.name,
        children: country.children.map((state) => {
          return { ...state, value: state.id, label: state.name };
        }),
      };
    }) ?? [],
});

const mapDispatchToProps = (dispatch: any) => ({});

const connector = connect(mapStateToProps, mapDispatchToProps);

// Define the props that connect will inject into the component
type PropsFromRedux = ConnectedProps<typeof connector>;

// Connect the component to Redux
export default connector(ProfilePage);
